// src/components/incident/IncidentReport.jsx
// Accessible via admin sidebar AND standalone at /incident
// Two tabs: Protocol reference card + Incident report form

import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useLocation } from '../../contexts/LocationContext';

const ORANGE = '#EE6123';
const NAVY = '#1A2E4A';

// ── Protocol Reference Card ───────────────────────────────────────────────────

const ProtocolCard = ({ number, title, color, items }) => (
  <div style={{ marginBottom: '0.75rem', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
    <div style={{ backgroundColor: color, padding: '0.5rem 1rem' }}>
      <span style={{ color: 'white', fontWeight: '800', fontSize: '0.9rem' }}>{number}. {title}</span>
    </div>
    <div style={{ backgroundColor: '#f9fafb' }}>
      {items.map((item, i) => (
        <div key={i} style={{ padding: '0.45rem 1.25rem', fontSize: '0.82rem', color: '#374151', borderBottom: i < items.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
          {item}
        </div>
      ))}
    </div>
  </div>
);

const ProtocolView = () => (
  <div style={{ maxWidth: '900px', margin: '0 auto' }}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
      <div>
        <ProtocolCard number="1" title="PRIORITY FIRST: Ensure Safety" color={NAVY} items={[
          '→ Stop the activity immediately',
          '→ Separate students involved',
          '→ Check for injuries',
          '→ Assign supervision to all students',
        ]} />
        <ProtocolCard number="2" title="ASSESS" color={ORANGE} items={[
          'Medical — Minor: first aid  |  Moderate: call parent  |  Severe: 911 then parent',
          'Behavioral — Separate calmly, no shaming, speak individually',
          'Pickup — Verify pickup, call parent, escalate after 5–10 min',
        ]} />
        <ProtocolCard number="3" title="COMMUNICATION ORDER" color={NAVY} items={[
          '1 → Ensure safety first',
          '2 → Notify Lead / Director',
          '3 → Contact parent / guardian',
          '4 → Document the incident',
        ]} />
        <ProtocolCard number="4" title="PARENT COMMUNICATION" color={ORANGE} items={[
          'Be factual and calm — do not speculate',
          'Explain what happened and what actions were taken',
          'Do not assign blame',
        ]} />
      </div>
      <div>
        <ProtocolCard number="5" title="DOCUMENTATION" color={NAVY} items={[
          'Complete incident report the same day — do not delay',
        ]} />
        <ProtocolCard number="6" title="ESCALATE IF" color={ORANGE} items={[
          'Head injury of any kind',
          'Repeated or serious aggression',
          'Parent / guardian is unreachable',
          'You are unsure how to handle the situation',
        ]} />
        <ProtocolCard number="7" title="DO NOT" color="#DC2626" items={[
          '✗ Ignore or minimize any incident',
          '✗ Attempt to diagnose injuries',
          '✗ Argue with parents or students',
          '✗ Leave any student unsupervised',
        ]} />
      </div>
    </div>
  </div>
);

// ── Incident Report Form ──────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.875rem',
  border: '1.5px solid #d1d5db', borderRadius: '7px', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white',
};

const labelStyle = {
  display: 'block', fontSize: '0.78rem', fontWeight: '700',
  color: '#374151', marginBottom: '0.3rem',
};

const sectionHead = (title) => (
  <div style={{ backgroundColor: NAVY, color: 'white', padding: '0.4rem 0.75rem', borderRadius: '6px', fontWeight: '700', fontSize: '0.82rem', marginBottom: '0.75rem', marginTop: '0.5rem' }}>
    {title}
  </div>
);

const Checkbox = ({ label, checked, onChange }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.83rem', color: '#374151', cursor: 'pointer', marginBottom: '0.3rem' }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
    {label}
  </label>
);

const defaultForm = {
  date: new Date().toISOString().split('T')[0],
  time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
  instructorName: '',
  studentNames: '',
  ageGroup: '',
  incidentType: { injury: false, behavioral: false, pickup: false, other: false },
  incidentTypeOther: '',
  whatHappened: '',
  actionFirstAid: false,
  actionSeparated: false,
  action911: false,
  actionOther: '',
  parentContacted: '',
  parentContactTime: '',
  parentContactMethod: '',
  parentContactSummary: '',
  followUp: '',
  instructorSignature: '',
};

const ReportForm = ({ locationId, locationLabel }) => {
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setType = (key) => setForm(f => ({ ...f, incidentType: { ...f.incidentType, [key]: !f.incidentType[key] } }));

  const validate = () => {
    const e = {};
    if (!form.date) e.date = 'Required';
    if (!form.time) e.time = 'Required';
    if (!form.instructorName.trim()) e.instructorName = 'Required';
    if (!form.studentNames.trim()) e.studentNames = 'Required';
    if (!Object.values(form.incidentType).some(Boolean)) e.incidentType = 'Select at least one';
    if (!form.whatHappened.trim()) e.whatHappened = 'Required';
    if (!form.instructorSignature.trim()) e.instructorSignature = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const incidentTypes = Object.entries(form.incidentType)
        .filter(([, v]) => v).map(([k]) => k);
      if (form.incidentTypeOther) incidentTypes.push(`other: ${form.incidentTypeOther}`);

      const actions = [];
      if (form.actionFirstAid) actions.push('First aid given');
      if (form.actionSeparated) actions.push('Students separated');
      if (form.action911) actions.push('911 called');
      if (form.actionOther) actions.push(form.actionOther);

      await addDoc(collection(db, 'incident_reports'), {
        date: form.date,
        time: form.time,
        instructorName: form.instructorName,
        studentNames: form.studentNames,
        ageGroup: form.ageGroup,
        incidentTypes,
        whatHappened: form.whatHappened,
        actionsTaken: actions,
        parentContacted: form.parentContacted,
        parentContactTime: form.parentContactTime,
        parentContactMethod: form.parentContactMethod,
        parentContactSummary: form.parentContactSummary,
        followUp: form.followUp,
        instructorSignature: form.instructorSignature,
        locationId,
        locationLabel,
        submittedAt: serverTimestamp(),
        status: 'submitted',
      });

      // Trigger admin email via Cloud Function — include location
      await fetch('https://us-central1-stemtree-pickup-app.cloudfunctions.net/notifyIncidentReport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: form.date,
          time: form.time,
          instructorName: form.instructorName,
          studentNames: form.studentNames,
          incidentTypes,
          whatHappened: form.whatHappened,
          actionsTaken: actions,
          locationId,
          locationLabel,
        }),
      });

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div style={{ maxWidth: '600px', margin: '3rem auto', textAlign: 'center', padding: '2rem', backgroundColor: '#f0fdf4', borderRadius: '14px', border: '2px solid #86efac' }}>
      <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
      <h2 style={{ color: '#065f46', fontWeight: '800', margin: '0 0 0.5rem' }}>Report Submitted</h2>
      <p style={{ color: '#374151', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>The incident has been recorded and admin has been notified.</p>
      <button onClick={() => { setForm(defaultForm); setSubmitted(false); }}
        style={{ padding: '0.65rem 1.5rem', backgroundColor: NAVY, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '0.875rem' }}>
        Submit Another Report
      </button>
    </div>
  );

  const err = (field) => errors[field]
    ? <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: '0.2rem' }}>{errors[field]}</div>
    : null;

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>

        {/* Basic Info */}
        {sectionHead('Basic Information')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Date *</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={inputStyle} />
            {err('date')}
          </div>
          <div>
            <label style={labelStyle}>Time *</label>
            <input type="time" value={form.time} onChange={e => set('time', e.target.value)} style={inputStyle} />
            {err('time')}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Instructor Name *</label>
            <input type="text" value={form.instructorName} onChange={e => set('instructorName', e.target.value)} placeholder="Your name" style={inputStyle} />
            {err('instructorName')}
          </div>
          <div>
            <label style={labelStyle}>Age / Group</label>
            <input type="text" value={form.ageGroup} onChange={e => set('ageGroup', e.target.value)} placeholder="e.g. K-2, Full-Day Camp" style={inputStyle} />
          </div>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Student Name(s) *</label>
          <input type="text" value={form.studentNames} onChange={e => set('studentNames', e.target.value)} placeholder="Full name(s) of student(s) involved" style={inputStyle} />
          {err('studentNames')}
        </div>

        {/* Incident Type */}
        {sectionHead('Incident Type')}
        <div style={{ marginBottom: '0.75rem' }}>
          <Checkbox label="Injury / Medical" checked={form.incidentType.injury} onChange={() => setType('injury')} />
          <Checkbox label="Behavioral" checked={form.incidentType.behavioral} onChange={() => setType('behavioral')} />
          <Checkbox label="Pickup / Admin" checked={form.incidentType.pickup} onChange={() => setType('pickup')} />
          <Checkbox label="Other:" checked={form.incidentType.other} onChange={() => setType('other')} />
          {form.incidentType.other && (
            <input type="text" value={form.incidentTypeOther} onChange={e => set('incidentTypeOther', e.target.value)} placeholder="Describe..." style={{ ...inputStyle, marginTop: '0.35rem' }} />
          )}
          {err('incidentType')}
        </div>

        {/* What Happened */}
        {sectionHead('What Happened (Facts Only)')}
        <div style={{ marginBottom: '0.75rem' }}>
          <textarea value={form.whatHappened} onChange={e => set('whatHappened', e.target.value)}
            placeholder="Describe exactly what happened — facts only, no opinions or diagnoses"
            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
          {err('whatHappened')}
        </div>

        {/* Actions Taken */}
        {sectionHead('Actions Taken')}
        <div style={{ marginBottom: '0.75rem' }}>
          <Checkbox label="First aid given" checked={form.actionFirstAid} onChange={() => set('actionFirstAid', !form.actionFirstAid)} />
          <Checkbox label="Separated students" checked={form.actionSeparated} onChange={() => set('actionSeparated', !form.actionSeparated)} />
          <Checkbox label="911 called" checked={form.action911} onChange={() => set('action911', !form.action911)} />
          <div style={{ marginTop: '0.35rem' }}>
            <label style={labelStyle}>Other action taken:</label>
            <input type="text" value={form.actionOther} onChange={e => set('actionOther', e.target.value)} placeholder="Describe..." style={inputStyle} />
          </div>
        </div>

        {/* Parent Communication */}
        {sectionHead('Parent Communication')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Parent Contacted</label>
            <input type="text" value={form.parentContacted} onChange={e => set('parentContacted', e.target.value)} placeholder="Parent name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Time of Contact</label>
            <input type="time" value={form.parentContactTime} onChange={e => set('parentContactTime', e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Method</label>
          <select value={form.parentContactMethod} onChange={e => set('parentContactMethod', e.target.value)} style={inputStyle}>
            <option value="">Select...</option>
            <option value="phone-call">Phone call</option>
            <option value="text">Text message</option>
            <option value="email">Email</option>
            <option value="in-person">In person</option>
            <option value="not-reached">Could not reach</option>
          </select>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>Summary of conversation</label>
          <textarea value={form.parentContactSummary} onChange={e => set('parentContactSummary', e.target.value)}
            placeholder="What was communicated to the parent..."
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} />
        </div>

        {/* Follow-up */}
        {sectionHead('Follow-Up Needed')}
        <div style={{ marginBottom: '0.75rem' }}>
          <textarea value={form.followUp} onChange={e => set('followUp', e.target.value)}
            placeholder="Any follow-up actions required (or leave blank if none)"
            style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
        </div>

        {/* Signature */}
        {sectionHead('Instructor Signature')}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Type your full name as signature *</label>
          <input type="text" value={form.instructorSignature} onChange={e => set('instructorSignature', e.target.value)} placeholder="Full name" style={inputStyle} />
          {err('instructorSignature')}
          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.25rem' }}>
            By typing your name you confirm this report is accurate to the best of your knowledge.
          </div>
        </div>

        <button onClick={handleSubmit} disabled={submitting} style={{
          width: '100%', padding: '0.9rem', backgroundColor: submitting ? '#9ca3af' : ORANGE,
          color: 'white', border: 'none', borderRadius: '9px', fontWeight: '800',
          fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer',
        }}>
          {submitting ? 'Submitting...' : 'Submit Incident Report'}
        </button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const IncidentReport = ({ defaultTab = 'protocol', onBack }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { currentLocation } = useLocation();

  const locationId = currentLocation === 'kirkland' ? 'kirkland' : 'redmond';
  const locationLabel = currentLocation === 'kirkland' ? 'Stemtree Kirkland' : 'Stemtree Redmond';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2d4a6e 100%)`, padding: '1.25rem 1.5rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {onBack && (
              <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '0.3rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                ← Back
              </button>
            )}
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', fontWeight: '600', letterSpacing: '0.08em' }}>
              {locationLabel.toUpperCase()}
            </div>
            <h1 style={{ color: 'white', fontWeight: '800', fontSize: '1.3rem', margin: 0 }}>Incident Management</h1>
          </div>
          <div style={{ backgroundColor: ORANGE, padding: '0.4rem 1rem', borderRadius: '6px' }}>
            <span style={{ color: 'white', fontWeight: '700', fontSize: '0.8rem' }}>📋 Staff Resource</span>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ backgroundColor: 'white', borderBottom: '2px solid #e5e7eb', padding: '0 1.5rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', gap: '0' }}>
          {[['protocol', '📋 Protocol Reference'], ['report', '📝 File Incident Report']].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '0.75rem 1.25rem', border: 'none',
              borderBottom: activeTab === tab ? `3px solid ${ORANGE}` : '3px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === tab ? ORANGE : '#6b7280',
              fontWeight: activeTab === tab ? '800' : '500',
              fontSize: '0.9rem', cursor: 'pointer',
            }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '1.5rem auto', padding: '0 1.5rem 3rem' }}>
        {activeTab === 'protocol'
          ? <ProtocolView />
          : <ReportForm locationId={locationId} locationLabel={locationLabel} />
        }
      </div>
    </div>
  );
};

export default IncidentReport;
